import type { ReactNode } from 'react';

import { isValidYear } from '../utils/string-utils';
import { theme } from '../styles/theme';

import type {
  HeatMapCountryMetaData,
  HeatMapRange,
  HeatMapRangeKey,
  HeatMapSexFilterOption,
} from './map-data-types';

export interface NccpLegendItem {
  color: string;
  translationKey: string;
}

export const nccpLegendConfig: NccpLegendItem[] = [
  { color: theme.colors.sand, translationKey: 'noData' },
  { color: '#b2dfdb', translationKey: 'hasNccp' },
  { color: '#4db6ac', translationKey: 'lungCancerInNccp' },
  { color: '#008b7f', translationKey: 'earlyDetectionInNccp' },
  { color: '#00695c', translationKey: 'fundingForEarlyDetection' },
];

const getNccpColorByKey = (key: string): string =>
  nccpLegendConfig.find(item => item.translationKey === key)?.color ??
  theme.colors.sand;

export interface CountryStatusConfig {
  key: string; // Filter option key (e.g., 'FormalCommitment')
  dataValue: string; // Data string (e.g., 'Formal commitment')
  translationKey: string;
  color: string;
  colorLight: string;
  colorLighter: string;
  textColor: string; // Text color for contrast on background
  stage: number;
  active: boolean; // disable to hide from UI
}

export const countryStatusConfig: CountryStatusConfig[] = [
  {
    key: 'UnknownStatus',
    dataValue: 'Unknown status',
    translationKey: 'unknownStatus',
    color: theme.colors['sand'],
    colorLight: theme.colors['sand-light'],
    colorLighter: theme.colors['sand-lighter'],
    textColor: theme.colors['blue-100'],
    stage: 1,
    active: true,
  },
  {
    key: 'ImplementationResearch',
    dataValue: 'Implementation research',
    translationKey: 'implementationResearch',
    color: theme.colors['pale-orange'],
    colorLight: theme.colors['pale-orange-light'],
    colorLighter: theme.colors['pale-orange-lighter'],
    textColor: theme.colors.white,
    stage: 2,
    active: true,
  },
  {
    key: 'FormalCommitment',
    dataValue: 'Formal commitment',
    translationKey: 'formalCommitment',
    color: theme.colors['blue-study'],
    colorLight: theme.colors['blue-study-light'],
    colorLighter: theme.colors['blue-study-lighter'],
    textColor: theme.colors.white,
    stage: 3,
    active: true,
  },
  {
    key: 'ImplementationRollout',
    dataValue: 'Implementation roll-out',
    translationKey: 'implementationRollout',
    color: theme.colors['yellow-100'],
    colorLight: theme.colors['yellow-100-light'],
    colorLighter: theme.colors['yellow-100-lighter'],
    textColor: theme.colors.white,
    stage: 4,
    active: true,
  },
  {
    key: 'RegionalRollout',
    dataValue: 'Regional roll-out',
    translationKey: 'regionalRollout',
    color: theme.colors['green-100'],
    colorLight: theme.colors['green-100-light'],
    colorLighter: theme.colors['green-100-lighter'],
    textColor: theme.colors.white,
    stage: 5,
    active: false,
  },
  {
    key: 'NationalRollout',
    dataValue: 'National roll-out',
    translationKey: 'nationalRollout',
    color: theme.colors['yellow-100'],
    colorLight: theme.colors['yellow-100-light'],
    colorLighter: theme.colors['yellow-100-lighter'],
    textColor: theme.colors.white,
    stage: 6,
    active: false,
  },
];

export const getStatusColorByValue = (statusValue: string): string =>
  countryStatusConfig.find(item => item.dataValue === statusValue)?.color ??
  theme.colors['green-100'];

// Lookup by data value string
export const getStatusConfigByDataValue = (
  dataValue: string,
): CountryStatusConfig | undefined =>
  countryStatusConfig.find(s => s.dataValue === dataValue);

// Map from data value to filter key (replaces duplicate maps elsewhere)
export const statusDataValueToKeyMap = Object.fromEntries(
  countryStatusConfig.map(s => [s.dataValue, s.key]),
) as Record<string, string>;

// Only active statuses (for UI display)
export const activeStatusConfig = countryStatusConfig.filter(s => s.active);

export const STATUS_TIMELINE_START_YEAR = 2015;

function parseYearFromString(
  yearStr: string | undefined | null,
): number | null {
  if (!yearStr || yearStr.trim() === '') return null;
  const match = yearStr.match(/^(\d{4})(?:-\d{2})?$/);
  return match ? Number.parseInt(match[1], 10) : null;
}

// Only these statuses show their actual colors; all others show neutral
const VISIBLE_STATUSES = new Set([
  'Formal commitment',
  'Implementation roll-out',
]);
const NEUTRAL_COLOR = theme.colors['sand'];

// Helper to get color only for visible statuses, otherwise neutral
const getVisibleStatusColor = (status: string): string =>
  VISIBLE_STATUSES.has(status) ? getStatusColorByValue(status) : NEUTRAL_COLOR;

export function getStatusColorAtYear(
  country: HeatMapCountryMetaData,
  selectedYear: number,
): string {
  const formalCommitmentYear = parseYearFromString(
    country.statusChangesOverTime,
  );
  const currentStatusYear = parseYearFromString(
    country.currentCountryStatusYear,
  );
  const currentStatus = country.countryStatus;

  if (formalCommitmentYear !== null && currentStatusYear !== null) {
    if (selectedYear < formalCommitmentYear) {
      return NEUTRAL_COLOR;
    }
    if (selectedYear < currentStatusYear) {
      return getStatusColorByValue('Formal commitment');
    }
    return getVisibleStatusColor(currentStatus);
  }

  if (formalCommitmentYear !== null) {
    if (selectedYear < formalCommitmentYear) {
      return NEUTRAL_COLOR;
    }
    return getVisibleStatusColor(currentStatus);
  }

  if (currentStatusYear !== null) {
    if (selectedYear < currentStatusYear) {
      return NEUTRAL_COLOR;
    }
    return getVisibleStatusColor(currentStatus);
  }

  return getVisibleStatusColor(currentStatus);
}

/** Timeline start year - countries without previousNccpPublished appear from this year */
export const NCCP_TIMELINE_START_YEAR = 1997;

/**
 * Get the current/final NCCP status color based on priority
 * Priority order (highest to lowest):
 * 1. Funding for earlier detection
 * 2. Earlier detection in NCCP
 * 3. Lung cancer in NCCP
 * 4. Has NCCP
 * 5. No data
 */
export function getNccpCurrentStatusColor(
  country: HeatMapCountryMetaData,
): string {
  if (country.fundingForEarlyDetection === 'Yes')
    return getNccpColorByKey('fundingForEarlyDetection');
  if (country.earlyDetectionInNccp === 'Yes')
    return getNccpColorByKey('earlyDetectionInNccp');
  if (country.lungCancerInNccp === 'Yes')
    return getNccpColorByKey('lungCancerInNccp');
  if (country.nccpAvailable === 'Yes') return getNccpColorByKey('hasNccp');
  return getNccpColorByKey('noData');
}

/**
 * Get the initial NCCP color when a country first appears on the timeline
 * Uses earlyDetectionInPreviousNccp for countries with previousNccpPublished,
 * otherwise falls back to basic NCCP availability status
 */
function getInitialNccpColor(country: HeatMapCountryMetaData): string {
  // If country has a first year AND early detection in previous NCCP, start with that color
  if (
    country.previousNccpPublished &&
    country.earlyDetectionInPreviousNccp === 'Yes'
  ) {
    return getNccpColorByKey('earlyDetectionInNccp');
  }

  // Fallback: basic NCCP availability
  if (country.nccpAvailable === 'Yes') {
    return getNccpColorByKey('hasNccp');
  }

  return getNccpColorByKey('noData');
}

/**
 * Get the NCCP color for a country at a specific year on the timeline
 *
 * Timeline logic:
 * - Countries WITH previousNccpPublished appear at that year
 * - Countries WITHOUT previousNccpPublished only appear when nccpLastUpdated is reached
 * - Countries with neither previousNccpPublished nor nccpLastUpdated are never shown
 * - Initial color is determined by earlyDetectionInPreviousNccp (if available) or basic NCCP status
 * - At nccpLastUpdated year (if set), color changes to current status
 *
 * @returns color string, or null if country should not be visible at this year
 */
export function getNccpColorAtYear(
  country: HeatMapCountryMetaData,
  selectedYear: number,
): string | null {
  const firstYearStr = country.previousNccpPublished;
  const lastUpdatedStr = country.nccpLastUpdated;
  const hasFirstYear = isValidYear(firstYearStr);
  const hasLastUpdated = isValidYear(lastUpdatedStr);

  // Countries without previousNccpPublished only appear when nccpLastUpdated is reached
  if (!hasFirstYear) {
    // If no lastUpdated either, never show
    if (!hasLastUpdated) {
      return null;
    }
    // Only show once we reach the lastUpdated year
    const lastUpdatedYear = Number.parseInt(lastUpdatedStr, 10);
    if (selectedYear < lastUpdatedYear) {
      return null;
    }
    // At or after lastUpdated, show current status
    return getNccpCurrentStatusColor(country);
  }

  // Country has previousNccpPublished - use that as appearance year
  const appearanceYear = Number.parseInt(firstYearStr, 10);

  // Country not visible yet
  if (selectedYear < appearanceYear) {
    return null;
  }

  // Determine if we've reached the "last updated" year for a potential color change
  if (hasLastUpdated) {
    const lastUpdatedYear = Number.parseInt(lastUpdatedStr, 10);
    if (selectedYear >= lastUpdatedYear) {
      // Use current status color (may be upgrade or downgrade)
      return getNccpCurrentStatusColor(country);
    }
  }

  // Still at initial state
  return getInitialNccpColor(country);
}

/** @deprecated Use getNccpCurrentStatusColor instead */
export function getNccpColor(country: HeatMapCountryMetaData): string {
  return getNccpCurrentStatusColor(country);
}

export interface LegendRange {
  color: string;
  label: string;
}

export interface LegendMeta {
  description: ReactNode;
}

export type HeatMapLegend = Record<HeatMapRangeKey, LegendRange>;
type HeatMapByFilter = Record<HeatMapSexFilterOption, HeatMapLegend>;

// Only incidence and mortality have standard range-based legends
// NCCP and Status use separate legend components with categorical coloring
type IncidenceMortalityRange = Exclude<HeatMapRange, 'nccp' | 'status'>;
type HeatMapLegends = Record<IncidenceMortalityRange, HeatMapByFilter>;

export const heatMapLegends: HeatMapLegends = {
  incidence: {
    Combined: {
      60: {
        color: '#0a4e9a',
        label: '≥ 26.9',
      },
      50: {
        color: '#3082bd',
        label: '17.1 - 26.9',
      },
      40: {
        color: '#6baed5',
        label: '9.0 - 17.1',
      },
      30: {
        color: '#9dc9e1',
        label: '3.7 - 9.0',
      },
      20: {
        color: '#c6dbf2',
        label: '< 3.7',
      },
      10: {
        color: theme.colors.sand,
        label: 'No data',
      },
    },
    Male: {
      60: {
        color: '#0a4e9a',
        label: '≥ 39.3',
      },
      50: {
        color: '#3082bd',
        label: '26.2 - 39.3',
      },
      40: {
        color: '#6baed5',
        label: '13.5 - 26.2',
      },
      30: {
        color: '#9dc9e1',
        label: '5.2 - 13.5',
      },
      20: {
        color: '#c6dbf2',
        label: '< 5.2',
      },
      10: {
        color: theme.colors.sand,
        label: 'No data',
      },
    },
    Female: {
      60: {
        color: '#0a4e9a',
        label: '≥ 15.6',
      },
      50: {
        color: '#3082bd',
        label: '7.8 - 15.6',
      },
      40: {
        color: '#6baed5',
        label: '4.9 - 7.8',
      },
      30: {
        color: '#9dc9e1',
        label: '2.8 - 4.9',
      },
      20: {
        color: '#c6dbf2',
        label: '< 2.8',
      },
      10: {
        color: theme.colors.sand,
        label: 'No data',
      },
    },
  },
  mortality: {
    Combined: {
      60: {
        color: '#a31016',
        label: '≥ 20.6',
      },
      50: {
        color: '#df2c26',
        label: '14.6 - 20.6',
      },
      40: {
        color: '#fc694b',
        label: '7.9 - 14.6',
      },
      30: {
        color: '#fd9372',
        label: '3.4 - 7.9',
      },
      20: {
        color: '#fbbea3',
        label: '< 3.4',
      },
      10: {
        color: theme.colors.sand,
        label: 'No data',
      },
    },
    Male: {
      60: {
        color: '#a31016',
        label: '≥ 32.9',
      },
      50: {
        color: '#df2c26',
        label: '21.5 - 32.9',
      },
      40: {
        color: '#fc694b',
        label: '11.5 - 21.5',
      },
      30: {
        color: '#fd9372',
        label: '4.8 - 11.5',
      },
      20: {
        color: '#fbbea3',
        label: '< 4.8',
      },
      10: {
        color: theme.colors.sand,
        label: 'No data',
      },
    },
    Female: {
      60: {
        color: '#a31016',
        label: '≥ 11.9',
      },
      50: {
        color: '#df2c26',
        label: '6.8 - 11.9',
      },
      40: {
        color: '#fc694b',
        label: '4.2 - 6.8',
      },
      30: {
        color: '#fd9372',
        label: '2.6 - 4.2',
      },
      20: {
        color: '#fbbea3',
        label: '< 2.6',
      },
      10: {
        color: theme.colors.sand,
        label: 'No data',
      },
    },
  },
};
